import { createFileRoute, Link } from "@tanstack/react-router";
import { consolidateGraph } from "@/lib/schema-graph";
import { Phone, MessageCircle, Mail, ShieldCheck, Globe2, Award, FileCheck2, MapPin, ArrowRight, Star } from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Translation, Notary & Visa Experts in Thailand | NYC";
const DESC =
  "Certified translation in 50+ languages, notary public, MFA legalization and visa support in Thailand. Free document check — same-day reply.";

export const Route = createFileRoute("/en/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/index", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/index", "en")},
      { name: "keywords", content: "translation Bangkok, notary public Thailand, MFA legalization, embassy attestation, NAATI translator Thailand, Thai visa, sworn translator" },
      { property: "og:title", content: ctaTitle(TITLE, "/en/index", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/index", "en")},
      { property: "og:url", content: `${SITE}/en` },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: ctaTitle(TITLE, "/en/index", "en")},
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en` },
      ...hreflangLinks("/", "/en"),
    ],
    scripts: [consolidateGraph([
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "NYC Legal & Notary Service Co., Ltd.",
          url: `${SITE}/en`,
          inLanguage: "en",
          telephone: "+66-83-249-4999",
          email: "contact@nyclegal.co.th",
          priceRange: "฿฿",
          areaServed: { "@type": "Country", name: "Thailand" },
          address: {
            "@type": "PostalAddress",
            streetAddress: "61 Soi Ladprao 95 (Prangthip), Wang Thonglang",
            addressLocality: "Bangkok",
            postalCode: "10310",
            addressCountry: "TH",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Do you translate documents accepted by embassies?", acceptedAnswer: { "@type": "Answer", text: "Yes — we are an officially recognized translator at the Thai Ministry of Foreign Affairs (MFA), most major embassies, and Australian NAATI (CPN9X0Q4P). Our translations are accepted globally." } },
            { "@type": "Question", name: "What is your turnaround time?", acceptedAnswer: { "@type": "Answer", text: "Standard 1–3 business days. Same-day and 4-hour rush available. We reply to inquiries within 15 minutes during business hours (Mon–Sat, 09:00–18:00 ICT)." } },
            { "@type": "Question", name: "Do you ship documents internationally?", acceptedAnswer: { "@type": "Answer", text: "Yes — DHL, FedEx, and EMS to 200+ countries with full tracking. Local Bangkok delivery same-day via in-house courier." } },
            { "@type": "Question", name: "Can foreigners use your visa services?", acceptedAnswer: { "@type": "Answer", text: "Absolutely. We assist Non-B work permits, LTR visa, Smart visa, Elite Card, DTV (Digital Nomad), marriage visa, retirement visa, and Permanent Residence applications.." } },
          ],
        }),
      },
    ])],
  }),
  component: EnPage,
});

const services = [
  { icon: FileCheck2, title: "Certified Translation", desc: "50+ languages · MFA accepted · Embassy & overseas use", href: "/en/services/translation" },
  { icon: ShieldCheck, title: "Notary Public", desc: "6 licensed attorneys · Thai Lawyers Council registered", href: "/en/services/notary-public" },
  { icon: Globe2, title: "NAATI Australia", desc: "Certified translator CPN9X0Q4P · DIBP accepted", href: "/en/services/naati" },
  { icon: Award, title: "MFA Legalization", desc: "Direct submission · 3–5 business days", href: "/en/services/mfa-legalization" },
  { icon: MapPin, title: "Embassy Attestation", desc: "40+ embassies · Hand-delivery service", href: "/en/services/embassy-legalization" },
  { icon: Star, title: "Visa Services", desc: "Non-B · LTR · Smart · Elite · DTV · Retirement", href: "/en/services/visa-foreigners-thailand" },
];

function EnPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] via-[#0d2150] to-[#0a1a3d] px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-6xl">
          <Link to="/" className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-500/20">
            🇹🇭 ภาษาไทย / Thai version
          </Link>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            Thailand's #1 Legal Translation,<br />
            <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">Notary & Visa Experts</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/80 sm:text-lg">
            15+ years serving expats, embassies, and Fortune 500 companies. 50+ languages · MFA & embassy legalization · 6 in-house attorneys · NAATI Australia. Reply in 15 minutes.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep shadow-xl shadow-amber-500/30 hover:bg-amber-400">
              <Phone className="h-4 w-4" /> Call +66 83 249 4999
            </a>
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white shadow-lg hover:bg-[#05B14C]">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <LeadFormModal trigger={
              <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white backdrop-blur hover:bg-white/10">
                <Mail className="h-4 w-4" /> Get Free Quote
              </button>
            } />
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-white/80">
            <span>⭐ 4.9 · 2,147 verified reviews</span>
            <span>• ISO 17100 certified</span>
            <span>• NAATI CPN9X0Q4P</span>
            <span>• DBD Gold (15+ years)</span>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Core Services</h2>
          <p className="mt-2 text-sm text-white/80">Full Thai-language pricing & detail pages available — click any card.</p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <Link key={s.title} to={s.href} className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all hover:-translate-y-0.5 hover:border-amber-400/40 hover:bg-white/[0.06]">
                <s.icon className="h-7 w-7 text-amber-300" />
                <h3 className="mt-4 text-base font-bold">{s.title}</h3>
                <p className="mt-1.5 text-sm text-white/70">{s.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-amber-300 group-hover:gap-2">
                  {`Learn about ${s.title}`} <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3 text-xs">
            <Link to="/en/services" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">All services →</Link>
            <Link to="/en/pricing" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Pricing</Link>
            <Link to="/en/faq" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">FAQ</Link>
            <Link to="/en/locations" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Coverage</Link>
            <Link to="/en/contact" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Contact</Link>
            <Link to="/en/apostille" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Apostille</Link>
            <Link to="/en/notary" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Notary</Link>
            <Link to="/en/translate" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Translate</Link>
            <Link to="/en/visa" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Visa</Link>
            <Link to="/en/marriage" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Marriage</Link>
            <Link to="/en/checklist" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Checklist</Link>
            <Link to="/en/embassy" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Embassy</Link>
            <Link to="/en/industries" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Industries</Link>
            <Link to="/en/blog" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Blog</Link>
            <Link to="/en/ask" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Ask</Link>
            <Link to="/en/reviews" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Reviews</Link>
            <Link to="/en/trust" className="rounded-full border border-white/15 bg-white/5 px-4 py-2 font-semibold text-white/80 hover:border-amber-400/40 hover:text-amber-200">Trust</Link>
            <Link to="/en/quote" className="rounded-full bg-amber-500 px-4 py-2 font-bold text-navy-deep hover:bg-amber-400">Get Quote →</Link>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="border-y border-white/10 bg-[#0d2150] px-4 py-16">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Why International Clients Choose NYC</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "50+", l: "Languages translated in-house" },
              { n: "127", l: "Service coverage areas (BKK + provinces)" },
              { n: "6", l: "Licensed Notary Public attorneys" },
              { n: "15min", l: "Average reply time during business hours" },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <div className="text-3xl font-extrabold text-amber-300">{s.n}</div>
                <p className="mt-1 text-sm text-white/70">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center sm:p-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Need it today? We deliver in 4 hours.</h2>
          <p className="mt-3 text-white/75">Same-day rush, embassy hand-delivery, and international DHL — all under one roof.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
            <a href="mailto:contact@nyclegal.co.th" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
              <Mail className="h-4 w-4" /> contact@nyclegal.co.th
            </a>
          </div>
          <p className="mt-6 text-xs text-white/80">
            For full Thai content, visit <Link to="/" className="underline hover:text-amber-300">หน้าภาษาไทย</Link>
          </p>
        </div>
      </section>
    </main>
  );
}
